import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Channel } from 'pusher-js';
import { Md5 } from 'ts-md5';
import { Observable, Subject, Subscription, throwError } from 'rxjs';
import { bufferTime, catchError, filter, tap } from 'rxjs/operators';

import { FundMessagesService } from '@state/fund-messages';
import { PusherService } from '@state/pusher';
import { RollbarService } from '@state/rollbar';
import { InvestorBulkEmailRepository } from './investor-bulk-email.repository';
import {
  INVESTOR_BULK_EMAIL_BUFFER_MS,
  INVESTOR_BULK_EMAIL_PROGRESS_EVENT,
  INVESTOR_BULK_EMAIL_SUCCESS_DISMISS_MS,
  InvestorBulkEmailFailedRecipient,
  InvestorBulkEmailProgressEvent,
  InvestorBulkEmailProgressStatus
} from './investor-bulk-email.model';
import { InvestorBulkEmailRequest } from '$interfaces/investor_bulk_email/request';
import { InvestorBulkEmailResponse } from '$interfaces/investor_bulk_email/response';


// Per-send bookkeeping that only the tracking logic needs; the store holds the
// derived counts the UI renders.
interface Tracker {
  channel: Channel;
  // personalization id -> recipient email
  emails: Map<string, string>;
  events$: Subject<InvestorBulkEmailProgressEvent>;
  // personalization ids already raised in Rollbar, so a failure reports once
  reported: Set<string>;
  // personalization id -> last known outcome
  statuses: Map<string, InvestorBulkEmailProgressStatus>;
  subscription: Subscription;
}

@Injectable({ providedIn: 'root' })
export class InvestorBulkEmailService {
  private fundMessagesService = inject(FundMessagesService);
  private investorBulkEmailRepository = inject(InvestorBulkEmailRepository);
  private pusherService = inject(PusherService);
  private rollbar = inject(RollbarService);

  private trackers = new Map<string, Tracker>();

  // Subscribes to the progress channel BEFORE firing the RPC: the backend
  // enqueues the per-recipient jobs in a post-commit hook, so the first
  // progress events can land before the HTTP response does.
  send$(fundName: string, request: InvestorBulkEmailRequest): Observable<InvestorBulkEmailResponse> {
    let { channel, vehicle_id: vehicleId } = request;

    this.startTracking({ channel, fundName, vehicleId });

    return this.fundMessagesService.send$(request).pipe(
      tap({
        next: ({ email_notification, email_personalizations }) =>
          this.setTotal(channel, email_personalizations.count, email_notification.id)
      }),
      catchError((error: HttpErrorResponse) => {
        // nothing was queued, so there is nothing to track
        this.stopTracking(channel);
        this.investorBulkEmailRepository.removeSend(channel);
        return throwError(() => error);
      })
    );
  }

  // Hides the card. An in-flight send keeps being tracked (failures still reach
  // Rollbar) and is dropped once complete; a finished one is dropped right away.
  dismiss(channel: string): void {
    let send = this.investorBulkEmailRepository.getSend(channel);
    if (!send) return;

    if (send.complete) {
      this.investorBulkEmailRepository.removeSend(channel);
    } else {
      this.investorBulkEmailRepository.updateSend(channel, { dismissed: true });
    }
  }

  startTracking({
    channel,
    fundName,
    vehicleId
  }: {
    channel: string;
    fundName: string;
    vehicleId: string;
  }): void {
    let events$ = new Subject<InvestorBulkEmailProgressEvent>();
    let pusherChannel = this.pusherService.pusher.subscribe(Md5.hashStr(channel));

    pusherChannel.bind(INVESTOR_BULK_EMAIL_PROGRESS_EVENT, (event: InvestorBulkEmailProgressEvent) =>
      events$.next(event)
    );

    // bufferTime rather than debounceTime: a steady stream of thousands of
    // events would keep a debounce from ever settling until the very end
    let subscription = events$
      .pipe(
        bufferTime(INVESTOR_BULK_EMAIL_BUFFER_MS),
        filter((batch) => batch.length > 0)
      )
      .subscribe({ next: (batch) => this.applyBatch(channel, batch) });

    this.trackers.set(channel, {
      channel: pusherChannel,
      emails: new Map(),
      events$,
      reported: new Set(),
      statuses: new Map(),
      subscription
    });

    this.investorBulkEmailRepository.addSend({
      channel,
      complete: false,
      dismissed: false,
      failed: [],
      fundName,
      hadIssues: false,
      retrying: 0,
      sent: 0,
      vehicleId
    });
  }

  applyBatch(channel: string, batch: InvestorBulkEmailProgressEvent[]): void {
    let tracker = this.trackers.get(channel);
    if (!tracker) return;

    for (let event of batch) {
      tracker.statuses.set(event.email_personalization_id, event.status);
      tracker.emails.set(event.email_personalization_id, event.email);
      // only the terminal failure is worth a report; the backend logs each retry itself
      if (event.status === 'failed') this.reportFailure(channel, event);
    }

    this.syncSend(channel);
  }

  reportFailure(channel: string, { email, email_personalization_id }: InvestorBulkEmailProgressEvent): void {
    let tracker = this.trackers.get(channel);
    if (!tracker || tracker.reported.has(email_personalization_id)) return;

    tracker.reported.add(email_personalization_id);

    let send = this.investorBulkEmailRepository.getSend(channel);

    this.rollbar.error('Investor bulk email delivery failed', {
      channel,
      email,
      email_notification_id: send?.emailNotificationId,
      email_personalization_id,
      vehicle_id: send?.vehicleId
    });
  }

  setTotal(channel: string, total: number, emailNotificationId: string): void {
    this.investorBulkEmailRepository.updateSend(channel, { emailNotificationId, total });
    // events that arrived before the response may already cover the total
    this.syncSend(channel);
  }

  // Derives the store entry from the tracker's per-recipient outcomes and ends
  // tracking once every expected recipient has reached a terminal outcome
  // (sent or failed). A recipient awaiting a retry keeps the send in flight.
  syncSend(channel: string): void {
    let tracker = this.trackers.get(channel);
    let send = this.investorBulkEmailRepository.getSend(channel);
    if (!tracker || !send) return;

    let failed: InvestorBulkEmailFailedRecipient[] = [];
    let retrying = 0;
    let sent = 0;

    for (let [email_personalization_id, status] of tracker.statuses) {
      if (status === 'sent') {
        sent += 1;
      } else if (status === 'pending') {
        retrying += 1;
      } else {
        failed.push({ email: tracker.emails.get(email_personalization_id), email_personalization_id });
      }
    }

    let complete = send.total !== undefined && sent + failed.length >= send.total;
    let hadIssues = send.hadIssues || retrying > 0 || failed.length > 0;

    if (complete && send.dismissed) {
      this.investorBulkEmailRepository.removeSend(channel);
    } else {
      this.investorBulkEmailRepository.updateSend(channel, { complete, failed, hadIssues, retrying, sent });
    }

    if (!complete) return;

    this.stopTracking(channel);

    // a clean send fades out on its own; one with failures waits to be read
    if (!send.dismissed && failed.length === 0) this.scheduleAutoDismiss(channel);
  }

  scheduleAutoDismiss(channel: string): void {
    // removing an already-dismissed send is a no-op, so a manual dismiss first is fine
    setTimeout(
      () => this.investorBulkEmailRepository.removeSend(channel),
      INVESTOR_BULK_EMAIL_SUCCESS_DISMISS_MS
    );
  }

  stopTracking(channel: string): void {
    let tracker = this.trackers.get(channel);
    if (!tracker) return;

    tracker.subscription.unsubscribe();
    tracker.events$.complete();
    tracker.channel.unbind(INVESTOR_BULK_EMAIL_PROGRESS_EVENT);
    tracker.channel.unsubscribe();
    this.trackers.delete(channel);
  }
}
